import { useGetServerlessProjects } from '@/service/serverless-gpu/hook';
import OnboardingInfoCreateProject from '../components/OnboardingInfoCreateProject';
import ServerlessProjectsTable from './project-table';
import { useServerlessStore } from '@/store/serverless';
import { useState, useEffect } from 'react';
import ProjectSearch from './project-search';
import dayjs from 'dayjs';
import { isDateInRange } from '@/utils/date';
import { DateRange } from 'react-day-picker';
import { useDebounce } from '@/hooks/useDebounce';
import { Button } from '@/components/ui/button';
import PATH_PAGE from '@/router/path';
import { Link, useParams } from 'react-router-dom';
import { RouteParams } from '@/router/type';
import { PlusIcon, RefreshCw, AlertCircle, CreditCard } from 'lucide-react';
import { useGetUserBalance } from '@/service/payment/hook';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { fCurrencyUSDExtended } from '@/utils/formatNumber';
import { useQuickTopup } from '@/hooks/useQuickTopup';
import { QuickTopupDialog } from '@/components/quick-topup/QuickTopupDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
type FormSchemaFilter = {
  searchString: string;
  status: string[];
  date: DateRange | undefined;
};

const STORAGE_KEY_PREFIX = 'serverless-projects-status-filter';

const ServerlessOverview = () => {
  const { orgId, workspaceId } = useParams<RouteParams>();
  const { token } = useServerlessStore();

  // Credit balance check
  const { data: creditData, isLoading: isLoadingCredit } = useGetUserBalance();
  const totalAvailableCredit = creditData?.balance ?? 0;
  const hasInsufficientBalance = totalAvailableCredit < 0.5;
  const { isOpen, setIsOpen, openTopup, onSuccess } = useQuickTopup();

  // Load saved status filter from localStorage on mount
  const getSavedStatusFilter = (): string[] => {
    if (!workspaceId) return [];
    try {
      const saved = localStorage.getItem(`${STORAGE_KEY_PREFIX}-${workspaceId}`);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading status filter from localStorage:', error);
      return [];
    }
  };

  const [filter, setFilter] = useState<FormSchemaFilter>({
    searchString: '',
    status: getSavedStatusFilter(),
    date: undefined,
  });
  const debouncedSearchString = useDebounce(filter.searchString, 500);

  // Save status filter to localStorage whenever it changes
  useEffect(() => {
    if (!workspaceId) return;
    try {
      localStorage.setItem(
        `${STORAGE_KEY_PREFIX}-${workspaceId}`,
        JSON.stringify(filter.status)
      );
    } catch (error) {
      console.error('Error saving status filter to localStorage:', error);
    }
  }, [filter.status, workspaceId]);

  const { data, isLoading, isFetching, refetch } = useGetServerlessProjects({
    enabled: !!token,
  });

  const dataTable =
    data?.projects
      ?.filter((project) => {
        // Case-insensitive substring search
        if (debouncedSearchString) {
          const searchLower = debouncedSearchString.toLowerCase();
          const projectIdLower = project.project_id.toLowerCase();
          const projectNameLower = project.project_name?.toLowerCase() || '';

          if (
            !projectIdLower.includes(searchLower) &&
            !projectNameLower.includes(searchLower)
          )
            return false;
        }
        if (filter.status.length > 0 && !filter.status.includes(project.status))
          return false;
        if (!isDateInRange(project.created_at, filter.date)) return false;
        return true;
      })
      .map((project) => ({
        id: project.project_id,
        projectId: project.project_id,
        projectName: project.project_name,
        createdAt: project.created_at,
        updatedAt: project.updated_at,
        instanceType: project.instance_type,
        status: project.status,
      })) || [];

  const isFoundProjects = data && data?.projects?.length > 0;

  // Calculate total active projects
  const totalActiveProjects = data?.projects?.filter(
    (project) => project.status === 'active'
  ).length || 0;

  return (
    <div>
      <div className="container mt-8">
        {/* Insufficient Balance Warning */}
        {!isLoadingCredit && hasInsufficientBalance && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Insufficient Credit Balance</AlertTitle>
            <AlertDescription className="mt-2">
              <p>
                Your current balance is {fCurrencyUSDExtended(totalAvailableCredit)}.
                Please top up your account to create new projects.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={openTopup}
              >
                Quick Top-up $5
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between gap-2">
          <div className="flex items-center gap-3">
            <p className="text-lg font-semibold">Projects</p>
            <div className="rounded-full bg-primary/10 px-3 py-1">
              <span className="text-sm font-medium text-primary">
                {totalActiveProjects} Active
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
              startIcon={<RefreshCw className={`size-4 ${isFetching ? 'animate-spin' : ''}`} />}
            >
              Refresh
            </Button>
            {hasInsufficientBalance && !isLoadingCredit ? (
              <Button
                variant="outline"
                startIcon={<CreditCard className="size-4" />}
                onClick={openTopup}
              >
                Topup & Create Project
              </Button>
            ) : (
              <Link
                to={PATH_PAGE.dashboard.serverlessGPU.createProject(orgId!, workspaceId!)}
              >
                <Button
                  variant="outline"
                  startIcon={<PlusIcon className="size-4" />}
                >
                  Create Project
                </Button>
              </Link>
            )}
          </div>
        </div>
        <div className="mt-4 grid gap-8">
          {isFoundProjects && (
            <>
              <ProjectSearch filter={filter} onFilterChange={setFilter} />
              <ServerlessProjectsTable data={dataTable} isLoading={isLoading} />
            </>
          )}
          {!isFoundProjects && !isLoading && (
            <>
              <OnboardingInfoCreateProject />
            </>
          )}
        </div>
      </div>

      <QuickTopupDialog open={isOpen} onOpenChange={setIsOpen} onSuccess={onSuccess} />
    </div>
  );
};

export default ServerlessOverview;
